// src/components/PriceNegotiation.tsx - SCRIPT COMPLET
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

import negotiationService, { 
  type PriceNegotiation, 
  PriceNegotiationCreate,
  ActiveNegotiationInfo 
} from '../services/negotiationService';

import { 
  DollarSign, 
  MessageCircle, 
  Clock, 
  CheckCircle, 
  X, 
  AlertCircle,
  TrendingDown,
  Handshake,
  Timer,
  RefreshCw,
  Plus,
  Minus
} from 'lucide-react';

interface PriceNegotiationProps {
  productId: number;
  productName: string;
  originalPrice: number;
  stock: number;
  isOwner: boolean;
  onNegotiationAccepted?: (finalPrice: number) => void;
}

const PriceNegotiation = ({ 
  productId, 
  productName, 
  originalPrice, 
  stock, 
  isOwner,
  onNegotiationAccepted 
}: PriceNegotiationProps) => {
  const { user } = useAuth();
  
  // États principaux
  const [activeNegotiation, setActiveNegotiation] = useState<ActiveNegotiationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // États du formulaire de proposition
  const [showProposeForm, setShowProposeForm] = useState(false);
  const [proposedPrice, setProposedPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  // États pour accepter contre-offre
  const [acceptingCounterOffer, setAcceptingCounterOffer] = useState(false);

  useEffect(() => {
    if (user?.is_customer && !isOwner) {
      loadActiveNegotiation();
    } else {
      setLoading(false);
    }
  }, [productId, user, isOwner]);

  const loadActiveNegotiation = async () => {
    try {
      setLoading(true);
      setError('');
      
      const negotiationInfo = await negotiationService.getActiveNegotiationForProduct(productId);
      setActiveNegotiation(negotiationInfo);
      
    } catch (error) {
      console.error('Erreur lors du chargement de la négociation:', error);
      setError('Erreur lors du chargement des informations de négociation');
    } finally {
      setLoading(false);
    }
  };

  const handleProposePrice = async () => {
    const proposedPriceNum = parseFloat(proposedPrice);
    
    // Validation
    const errors = negotiationService.validateProposal(proposedPriceNum, originalPrice, quantity);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    if (quantity > stock) {
      setError(`Stock insuffisant. Disponible: ${stock}`);
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      
      const negotiationData: PriceNegotiationCreate = {
        product_id: productId,
        proposed_price: proposedPriceNum,
        quantity: quantity
      };
      
      await negotiationService.proposePrice(negotiationData);
      
      // Recharger les informations
      await loadActiveNegotiation();
      
      setSuccess(`Proposition envoyée ! Vous proposez ${negotiationService.formatPrice(proposedPriceNum)} pour ${quantity} unité(s).`);
      setShowProposeForm(false);
      setProposedPrice('');
      setQuantity(1);
      
    } catch (error: any) {
      console.error('Erreur lors de la proposition:', error);
      
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Erreur lors de l\'envoi de la proposition. Veuillez réessayer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptCounterOffer = async () => {
    if (!activeNegotiation?.negotiation) return;

    try {
      setAcceptingCounterOffer(true);
      setError('');
      setSuccess('');
      
      const result = await negotiationService.acceptCounterOffer(activeNegotiation.negotiation.id);
      
      // Notifier le parent de l'acceptation
      if (onNegotiationAccepted && activeNegotiation.negotiation.counter_offer_price) {
        onNegotiationAccepted(activeNegotiation.negotiation.counter_offer_price);
      }
      
      // Recharger les informations
      await loadActiveNegotiation();
      
      setSuccess('Contre-offre acceptée ! Vous pouvez maintenant procéder à l\'achat au prix négocié.');
      
    } catch (error: any) {
      console.error('Erreur lors de l\'acceptation:', error);
      
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Erreur lors de l\'acceptation. Veuillez réessayer.');
      }
    } finally {
      setAcceptingCounterOffer(false);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= stock) {
      setQuantity(newQuantity);
    }
  };

  const calculateDiscount = (proposedPrice: number): number => {
    return ((originalPrice - proposedPrice) / originalPrice) * 100;
  };

  const formatTimeLeft = (expiresAt: string): string => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expirée';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Ne pas afficher si l'utilisateur n'est pas connecté ou n'est pas customer ou est propriétaire
  if (!user?.is_customer || isOwner) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-blue-800">Chargement des négociations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Messages d'erreur et succès */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800 text-sm">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 text-sm">{success}</span>
        </div>
      )}

      {/* Négociation active */}
      {activeNegotiation?.has_active_negotiation && activeNegotiation.negotiation ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <MessageCircle className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-slate">Négociation en cours</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              negotiationService.getStatusColor(activeNegotiation.negotiation.status)
            }`}>
              {negotiationService.getStatusLabel(activeNegotiation.negotiation.status)}
            </span>
          </div>

          <div className="space-y-3">
            {/* Informations de base */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Prix original:</span>
                <div className="font-medium">{negotiationService.formatPrice(originalPrice)}</div>
              </div>
              <div>
                <span className="text-gray-600">Votre proposition:</span>
                <div className="font-medium text-blue-600">
                  {negotiationService.formatPrice(activeNegotiation.negotiation.proposed_price)}
                  <span className="text-xs text-green-600 ml-1">
                    (-{calculateDiscount(activeNegotiation.negotiation.proposed_price).toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div>
                <span className="text-gray-600">Quantité:</span>
                <div className="font-medium">{activeNegotiation.negotiation.quantity} unité(s)</div>
              </div>
              <div>
                <span className="text-gray-600">Propositions restantes:</span>
                <div className="font-medium">
                  {activeNegotiation.proposals_left || 0} / 3
                </div>
              </div>
            </div>

            {/* Timer d'expiration */}
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <div className="flex items-center gap-2 text-yellow-800">
                <Timer className="h-4 w-4" />
                <span className="text-sm">
                  Expire dans: {formatTimeLeft(activeNegotiation.negotiation.expires_at)}
                </span>
              </div>
            </div>

            {/* Contre-offre du farmer */}
            {activeNegotiation.negotiation.status === 'counter_offered' && activeNegotiation.negotiation.counter_offer_price && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Handshake className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">Contre-offre du producteur</h4>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <span className="text-blue-700 text-sm">Prix proposé par le farmer:</span>
                    <div className="text-xl font-bold text-blue-900">
                      {negotiationService.formatPrice(activeNegotiation.negotiation.counter_offer_price)}
                      <span className="text-sm text-green-600 ml-2">
                        (-{calculateDiscount(activeNegotiation.negotiation.counter_offer_price).toFixed(1)}% du prix original)
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleAcceptCounterOffer}
                      disabled={acceptingCounterOffer}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {acceptingCounterOffer ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Accepter la contre-offre
                    </button>
                    
                    {activeNegotiation.can_propose_again && (
                      <button
                        onClick={() => setShowProposeForm(true)}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300"
                      >
                        Nouvelle proposition
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions pour négociation pending */}
            {activeNegotiation.negotiation.status === 'pending' && (
              <div className="bg-gray-50 rounded p-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">En attente de la réponse du producteur...</span>
                </div>
                {activeNegotiation.can_propose_again && (
                  <button
                    onClick={() => setShowProposeForm(true)}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    Modifier ma proposition
                  </button>
                )}
              </div>
            )}

            {/* Négociation acceptée */}
            {activeNegotiation.negotiation.status === 'accepted' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold text-green-900">Négociation acceptée !</h4>
                </div>
                <p className="text-green-800 text-sm mb-3">
                  Le producteur a accepté votre proposition. Vous pouvez maintenant procéder à l'achat au prix négocié.
                </p>
                <div className="text-lg font-bold text-green-900">
                  Prix final: {negotiationService.formatPrice(negotiationService.getFinalPrice(activeNegotiation.negotiation))}
                  <span className="text-sm text-green-600 ml-2">
                    (Économie: {negotiationService.formatPrice(negotiationService.calculateSavings(activeNegotiation.negotiation))})
                  </span>
                </div>
              </div>
            )}

            {/* Négociation rejetée */}
            {activeNegotiation.negotiation.status === 'rejected' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <X className="h-5 w-5 text-red-600" />
                  <h4 className="font-semibold text-red-900">Proposition refusée</h4>
                </div>
                <p className="text-red-800 text-sm">
                  {activeNegotiation.can_propose_again 
                    ? `Le producteur a refusé votre proposition. Vous pouvez encore faire ${activeNegotiation.proposals_left} proposition(s).`
                    : 'Le producteur a refusé votre proposition. Limite de propositions atteinte.'}
                </p>
                {activeNegotiation.can_propose_again && (
                  <button
                    onClick={() => setShowProposeForm(true)}
                    className="mt-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition duration-300"
                  >
                    Faire une nouvelle proposition
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Bouton pour démarrer une négociation */
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <TrendingDown className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-slate">Négocier le prix</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Proposez votre prix au producteur ! Vous avez jusqu'à 3 tentatives pour trouver un accord.
          </p>
          <button
            onClick={() => setShowProposeForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300 flex items-center gap-2"
          >
            <DollarSign className="h-4 w-4" />
            Proposer un prix
          </button>
        </div>
      )}

      {/* Formulaire de proposition */}
      {showProposeForm && (
        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-slate">Proposer un prix</h4>
            <button
              onClick={() => setShowProposeForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Prix proposé (FCFA) *
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Moins de ${originalPrice}`}
                  min="1"
                  max={originalPrice - 1}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Prix actuel: {negotiationService.formatPrice(originalPrice)}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Quantité *
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  disabled={quantity <= 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Minus className="h-4 w-4" />
                </button>
                
                <span className="w-16 text-center font-medium text-lg">
                  {quantity}
                </span>
                
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  disabled={quantity >= stock}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Stock disponible: {stock} unité(s)
              </div>
            </div>

            {/* Aperçu de l'économie */}
            {proposedPrice && parseFloat(proposedPrice) > 0 && parseFloat(proposedPrice) < originalPrice && (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <div className="text-sm text-green-800">
                  <strong>Économie potentielle:</strong> {negotiationService.formatPrice(originalPrice - parseFloat(proposedPrice))} 
                  <span className="ml-1">({calculateDiscount(parseFloat(proposedPrice)).toFixed(1)}% de réduction)</span>
                </div>
                <div className="text-sm text-green-700 mt-1">
                  <strong>Total proposé:</strong> {negotiationService.formatPrice(parseFloat(proposedPrice) * quantity)}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowProposeForm(false)}
                className="flex-1 bg-gray-200 text-slate px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300"
              >
                Annuler
              </button>
              <button
                onClick={handleProposePrice}
                disabled={submitting || !proposedPrice || parseFloat(proposedPrice) >= originalPrice}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <DollarSign className="h-4 w-4" />
                )}
                Envoyer la proposition
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceNegotiation;